import { useState, useMemo } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useLanguage } from '../context/LanguageContext'

/* ─── Constants ─────────────────────────────────────────────── */
const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

// UAE Banks with typical rates (fixed 3yr then variable)
const UAE_BANKS = [
  { name: 'Emirates NBD',   fixedRate: 3.99, varMargin: 1.00 },
  { name: 'ADCB',           fixedRate: 3.95, varMargin: 1.10 },
  { name: 'FAB',            fixedRate: 4.09, varMargin: 1.20 },
  { name: 'Mashreq',        fixedRate: 3.99, varMargin: 1.29 },
  { name: 'DIB',            fixedRate: 4.25, varMargin: 1.40 },
  { name: 'HSBC UAE',       fixedRate: 4.49, varMargin: 1.49 },
  { name: 'Standard Chartered', fixedRate: 4.39, varMargin: 1.35 },
]

const EIBOR_3M = 4.24 // EIBOR 3M as of reference

/* ─── Helpers ───────────────────────────────────────────────── */
const fmt = (n) =>
  new Intl.NumberFormat('en-AE', { style: 'decimal', maximumFractionDigits: 0 }).format(n)

const fmtDec = (n, d = 2) =>
  new Intl.NumberFormat('en-AE', { minimumFractionDigits: d, maximumFractionDigits: d }).format(n)

/** Standard amortization formula. Returns monthly EMI. */
function calcEMI(principal, annualRate, months) {
  if (annualRate === 0) return principal / months
  const r = annualRate / 100 / 12
  return (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1)
}

/** Build amortization schedule (yearly summary rows). */
function buildAmortSchedule(principal, annualRate, months) {
  const r = annualRate / 100 / 12
  const emi = calcEMI(principal, annualRate, months)
  let balance = principal
  const yearly = []
  let yearPrincipal = 0, yearInterest = 0

  for (let m = 1; m <= months; m++) {
    const interest = balance * r
    const princ = emi - interest
    balance -= princ
    yearPrincipal += princ
    yearInterest += interest

    if (m % 12 === 0 || m === months) {
      yearly.push({
        year: Math.ceil(m / 12),
        principal: yearPrincipal,
        interest: yearInterest,
        balance: Math.max(0, balance),
      })
      yearPrincipal = 0
      yearInterest = 0
    }
  }
  return { emi, schedule: yearly }
}

/* ─── Shared Sub-Components ─────────────────────────────────── */
const Label = ({ children }) => (
  <label style={{
    display: 'block', fontFamily: FONT, fontSize: '13px',
    fontWeight: 600, color: NAVY, marginBottom: '8px',
  }}>{children}</label>
)

const baseInput = {
  width: '100%', padding: '13px 16px', border: `1.5px solid #dde2ea`,
  borderRadius: '10px', fontFamily: FONT, fontSize: '14px', color: NAVY,
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
}

const ResultBox = ({ label, value, highlight }) => (
  <div style={{
    background: highlight ? NAVY : '#f4f7fb',
    borderRadius: '12px', padding: '18px 22px',
    borderLeft: `4px solid ${highlight ? GOLD : 'transparent'}`,
  }}>
    <div style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 500, color: highlight ? 'rgba(255,255,255,0.7)' : '#8a9ab0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
      {label}
    </div>
    <div style={{ fontFamily: FONT, fontSize: highlight ? '26px' : '20px', fontWeight: 700, color: highlight ? '#fff' : NAVY, lineHeight: 1.2 }}>
      {value}
    </div>
  </div>
)

/* ─── TAB 1: Mortgage Calculator ────────────────────────────── */
function MortgageCalculator({ m }) {
  const [propertyValue, setPropertyValue] = useState(2000000)
  const [downPct, setDownPct] = useState(20)
  const [rate, setRate] = useState(3.99)
  const [years, setYears] = useState(25)
  const [showTable, setShowTable] = useState(false)
  const [showComparison, setShowComparison] = useState(false)

  const loanAmount = propertyValue * (1 - downPct / 100)
  const months = years * 12
  const { emi, schedule } = useMemo(() => buildAmortSchedule(loanAmount, rate, months), [loanAmount, rate, months])
  const totalPayable = emi * months
  const totalInterest = totalPayable - loanAmount
  const downPayment = propertyValue - loanAmount

  return (
    <div>
      <style>{`
        .mc-input:focus { border-color: ${NAVY} !important; box-shadow: 0 0 0 3px rgba(0,46,83,0.1) !important; }
        .mc-range { -webkit-appearance: none; appearance: none; height: 5px; border-radius: 3px; outline: none; cursor: pointer; }
        .mc-range::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: ${NAVY}; border: 3px solid #fff; box-shadow: 0 2px 8px rgba(0,46,83,0.35); cursor: pointer; }
        .mc-tab-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
        .mc-result-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        @media (max-width: 900px) { .mc-grid { grid-template-columns: 1fr !important; } .el-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 640px) { .mc-tab-row { grid-template-columns: 1fr; } .mc-result-grid { grid-template-columns: 1fr !important; } }
        .amort-table { width: 100%; border-collapse: collapse; font-family: ${FONT}; font-size: 13px; }
        .amort-table th { background: ${NAVY}; color: #fff; padding: 10px 14px; text-align: right; font-weight: 600; }
        .amort-table th:first-child { text-align: left; }
        .amort-table td { padding: 10px 14px; border-bottom: 1px solid #f0f3f7; color: #333; text-align: right; }
        .amort-table td:first-child { text-align: left; font-weight: 600; color: ${NAVY}; }
        .amort-table tr:hover td { background: #f8fafc; }
        .bank-row:hover { background: #f4f7fb !important; }
      `}</style>

      <div className="mc-grid" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '32px', alignItems: 'start' }}>

        {/* LEFT — Inputs */}
        <div style={{ background: '#fff', borderRadius: '16px', padding: '32px 36px', boxShadow: '0 2px 18px rgba(0,46,83,0.07)', borderTop: `4px solid ${NAVY}` }}>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 26px' }}>{m.loanParams}</h3>

          {/* Property Value */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Label>{m.propertyValue}</Label>
              <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: GOLD }}>AED {fmt(propertyValue)}</span>
            </div>
            <input type="range" className="mc-range" min={200000} max={20000000} step={50000}
              value={propertyValue} onChange={e => setPropertyValue(+e.target.value)}
              style={{ width: '100%', background: `linear-gradient(to right, ${NAVY} ${((propertyValue - 200000) / 19800000) * 100}%, #dde2ea 0%)` }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '4px' }}>
              <span>200K</span><span>20M</span>
            </div>
          </div>

          {/* Down Payment */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Label>{m.downPayment}</Label>
              <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: GOLD }}>{downPct}% — AED {fmt(downPayment)}</span>
            </div>
            <input type="range" className="mc-range" min={20} max={80} step={1}
              value={downPct} onChange={e => setDownPct(+e.target.value)}
              style={{ width: '100%', background: `linear-gradient(to right, ${NAVY} ${((downPct - 20) / 60) * 100}%, #dde2ea 0%)` }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '4px' }}>
              <span>20% (min)</span><span>80%</span>
            </div>
          </div>

          {/* Interest Rate */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Label>{m.annualRate}</Label>
              <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: GOLD }}>{fmtDec(rate)}%</span>
            </div>
            <input type="range" className="mc-range" min={2.5} max={8} step={0.05}
              value={rate} onChange={e => setRate(+e.target.value)}
              style={{ width: '100%', background: `linear-gradient(to right, ${NAVY} ${((rate - 2.5) / 5.5) * 100}%, #dde2ea 0%)` }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '4px' }}>
              <span>2.5%</span><span>8.0%</span>
            </div>
          </div>

          {/* Loan Tenure */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Label>{m.loanTenure}</Label>
              <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: GOLD }}>{m.years(years)}</span>
            </div>
            <input type="range" className="mc-range" min={5} max={25} step={1}
              value={years} onChange={e => setYears(+e.target.value)}
              style={{ width: '100%', background: `linear-gradient(to right, ${NAVY} ${((years - 5) / 20) * 100}%, #dde2ea 0%)` }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '4px' }}>
              <span>5 Yrs</span><span>{m.maxUAE}</span>
            </div>
          </div>
        </div>

        {/* RIGHT — Results */}
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px', marginBottom: '14px' }}>
            <ResultBox label={m.monthlyEMI} value={`AED ${fmt(emi)}`} highlight />
            <div className="mc-result-grid">
              <ResultBox label={m.loanAmount} value={`AED ${fmt(loanAmount)}`} />
              <ResultBox label={m.downPayment} value={`AED ${fmt(downPayment)}`} />
              <ResultBox label={m.totalInterest} value={`AED ${fmt(totalInterest)}`} />
              <ResultBox label={m.totalPayable} value={`AED ${fmt(totalPayable)}`} />
            </div>
          </div>

          {/* Visual bar */}
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px 22px', boxShadow: '0 2px 12px rgba(0,46,83,0.07)' }}>
            <div style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: '#8a9ab0', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {m.principalVsInterest}
            </div>
            <div style={{ height: '12px', borderRadius: '6px', overflow: 'hidden', display: 'flex', marginBottom: '12px' }}>
              <div style={{ width: `${(loanAmount / totalPayable) * 100}%`, background: NAVY, transition: 'width 0.5s ease' }} />
              <div style={{ flex: 1, background: GOLD, transition: 'flex 0.5s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: FONT, fontSize: '12px', color: '#5a6577' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: NAVY, display: 'inline-block' }} />
                {m.principal} ({fmtDec((loanAmount / totalPayable) * 100, 1)}%)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: FONT, fontSize: '12px', color: '#5a6577' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: GOLD, display: 'inline-block' }} />
                {m.interest} ({fmtDec((totalInterest / totalPayable) * 100, 1)}%)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Comparison */}
      <div style={{ marginTop: '28px', background: '#fff', borderRadius: '16px', boxShadow: '0 2px 18px rgba(0,46,83,0.07)', overflow: 'hidden' }}>
        <button
          onClick={() => setShowComparison(p => !p)}
          style={{
            width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '20px 28px', background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY,
          }}
        >
          <span>{m.compareRates}</span>
          <span style={{ fontSize: '20px', transform: showComparison ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.25s' }}>⌃</span>
        </button>
        {showComparison && (
          <div style={{ padding: '0 28px 24px' }}>
            <p style={{ fontFamily: FONT, fontSize: '13px', color: '#8a9ab0', margin: '0 0 16px' }}>
              {m.basedOnLoan(fmt(loanAmount), years)}
            </p>
            <table className="amort-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', borderRadius: '8px 0 0 0' }}>{m.bankCol}</th>
                  <th>{m.fixedRate3yr}</th>
                  <th>{m.variableEIBOR}</th>
                  <th>{m.monthlyEMIFixed}</th>
                  <th style={{ borderRadius: '0 8px 0 0' }}>{m.totalCost}</th>
                </tr>
              </thead>
              <tbody>
                {UAE_BANKS.map((b, i) => {
                  const emiFixed = calcEMI(loanAmount, b.fixedRate, months)
                  const varRate = b.varMargin + EIBOR_3M
                  return (
                    <tr key={i} className="bank-row" style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td>{b.name}</td>
                      <td>{fmtDec(b.fixedRate)}%</td>
                      <td>{fmtDec(b.varMargin)}% + EIBOR ({fmtDec(varRate)}%)</td>
                      <td>AED {fmt(emiFixed)}</td>
                      <td>AED {fmt(emiFixed * months)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <p style={{ fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '12px' }}>
              {m.eibor3mNote(fmtDec(EIBOR_3M))}
            </p>
          </div>
        )}
      </div>

      {/* Amortization Table */}
      <div style={{ marginTop: '20px', background: '#fff', borderRadius: '16px', boxShadow: '0 2px 18px rgba(0,46,83,0.07)', overflow: 'hidden' }}>
        <button
          onClick={() => setShowTable(p => !p)}
          style={{
            width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '20px 28px', background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY,
          }}
        >
          <span>{m.amortSchedule}</span>
          <span style={{ fontSize: '20px', transform: showTable ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.25s' }}>⌃</span>
        </button>
        {showTable && (
          <div style={{ padding: '0 28px 24px', overflowX: 'auto' }}>
            <table className="amort-table">
              <thead>
                <tr>
                  <th style={{ borderRadius: '8px 0 0 0' }}>{m.yearCol}</th>
                  <th>{m.principalPaid}</th>
                  <th>{m.interestPaid}</th>
                  <th style={{ borderRadius: '0 8px 0 0' }}>{m.balanceRemaining}</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td>{m.yearRow(row.year)}</td>
                    <td>AED {fmt(row.principal)}</td>
                    <td>AED {fmt(row.interest)}</td>
                    <td>AED {fmt(row.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── TAB 2: Eligibility Calculator ─────────────────────────── */
function EligibilityCalculator({ m }) {
  const [residency, setResidency] = useState('resident') // resident | nonresident | national
  const [income, setIncome] = useState(25000)
  const [otherIncome, setOtherIncome] = useState(0)
  const [existingEMI, setExistingEMI] = useState(0)
  const [creditCard, setCreditCard] = useState(0)
  const [age, setAge] = useState(35)
  const [rate, setRate] = useState(3.99)
  const [result, setResult] = useState(null)

  // UAE CB rules:
  // DBR (Debt Burden Ratio) max 50% of gross monthly salary
  // Max tenor: 25yrs for residents/nationals, 15yrs for non-residents
  // Age at end of mortgage: max 65 (salaried) / 70 (self-employed) — use 65
  const calculate = () => {
    const maxTenorByAge = Math.max(0, 65 - age)
    const maxTenorByRule = residency === 'nonresident' ? 15 : 25
    const tenorYears = Math.min(maxTenorByAge, maxTenorByRule)
    const months = tenorYears * 12

    const grossIncome = income + otherIncome
    // Credit card limit counts as 5% of limit as monthly obligation
    const ccObligation = creditCard * 0.05
    const totalObligations = existingEMI + ccObligation
    // Max allowable EMI = 50% of gross income minus existing obligations
    const maxEMI = grossIncome * 0.50 - totalObligations

    if (maxEMI <= 0 || tenorYears <= 0) {
      setResult({ error: true })
      return
    }

    // Reverse EMI formula to get max loan
    const r = rate / 100 / 12
    const maxLoan = r === 0
      ? maxEMI * months
      : (maxEMI * (Math.pow(1 + r, months) - 1)) / (r * Math.pow(1 + r, months))

    // LTV: residents 80%, nationals 85%, non-residents 50%
    const ltvPct = residency === 'national' ? 0.85 : residency === 'nonresident' ? 0.50 : 0.80
    // Min down payment implies max property value = loan / LTV
    const maxPropertyValue = maxLoan / ltvPct
    const minDownPayment = maxPropertyValue - maxLoan

    setResult({ maxLoan, maxPropertyValue, minDownPayment, tenorYears, maxEMI, ltvPct, grossIncome, totalObligations })
  }

  const RESIDENCY_OPTS = m.residencyOpts

  return (
    <div>
      <style>{`
        .el-input:focus { border-color: ${NAVY} !important; box-shadow: 0 0 0 3px rgba(0,46,83,0.1) !important; }
        @media (max-width: 900px) { .el-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '32px', alignItems: 'start' }} className="el-grid">

        {/* LEFT — Inputs */}
        <div style={{ background: '#fff', borderRadius: '16px', padding: '32px 36px', boxShadow: '0 2px 18px rgba(0,46,83,0.07)', borderTop: `4px solid ${GOLD}` }}>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 24px' }}>{m.financialProfile}</h3>

          {/* Residency */}
          <div style={{ marginBottom: '22px' }}>
            <Label>{m.residencyStatus}</Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
              {RESIDENCY_OPTS.map(opt => (
                <button key={opt.value} type="button" onClick={() => setResidency(opt.value)}
                  style={{
                    border: `2px solid ${residency === opt.value ? NAVY : '#dde2ea'}`,
                    borderRadius: '10px', padding: '12px 8px', background: residency === opt.value ? `${NAVY}08` : '#fff',
                    cursor: 'pointer', textAlign: 'center', transition: 'all 0.18s',
                  }}>
                  <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY, marginBottom: '4px' }}>{opt.label}</div>
                  <div style={{ fontFamily: FONT, fontSize: '11px', color: '#8a9ab0' }}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Age */}
          <div style={{ marginBottom: '22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Label>{m.yourAge}</Label>
              <span style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 700, color: GOLD }}>{age} {m.ageUnit}</span>
            </div>
            <input type="range" className="mc-range" min={21} max={60} step={1} value={age}
              onChange={e => setAge(+e.target.value)}
              style={{ width: '100%', background: `linear-gradient(to right, ${NAVY} ${((age - 21) / 39) * 100}%, #dde2ea 0%)`, height: '5px', borderRadius: '3px', outline: 'none', cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '4px' }}>
              <span>21</span><span>60</span>
            </div>
          </div>

          {/* Income */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '22px' }}>
            <div>
              <Label>{m.monthlySalary}</Label>
              <input className="el-input" type="number" min={0} placeholder="e.g. 25000"
                value={income} onChange={e => setIncome(+e.target.value)}
                style={baseInput} />
            </div>
            <div>
              <Label>{m.otherIncome}</Label>
              <input className="el-input" type="number" min={0} placeholder="Rental, bonus…"
                value={otherIncome} onChange={e => setOtherIncome(+e.target.value)}
                style={baseInput} />
            </div>
          </div>

          {/* Liabilities */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '22px' }}>
            <div>
              <Label>{m.existingEMI}</Label>
              <input className="el-input" type="number" min={0} placeholder="Car, personal loans…"
                value={existingEMI} onChange={e => setExistingEMI(+e.target.value)}
                style={baseInput} />
            </div>
            <div>
              <Label>{m.creditCard}</Label>
              <input className="el-input" type="number" min={0} placeholder="Total limit"
                value={creditCard} onChange={e => setCreditCard(+e.target.value)}
                style={baseInput} />
              <p style={{ fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', margin: '4px 0 0' }}>{m.ccNote}</p>
            </div>
          </div>

          {/* Rate */}
          <div style={{ marginBottom: '28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Label>{m.assumedRate}</Label>
              <span style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 700, color: GOLD }}>{fmtDec(rate)}%</span>
            </div>
            <input type="range" className="mc-range" min={2.5} max={8} step={0.05} value={rate}
              onChange={e => setRate(+e.target.value)}
              style={{ width: '100%', background: `linear-gradient(to right, ${NAVY} ${((rate - 2.5) / 5.5) * 100}%, #dde2ea 0%)`, height: '5px', borderRadius: '3px', outline: 'none', cursor: 'pointer' }}
            />
          </div>

          <button onClick={calculate}
            style={{
              width: '100%', padding: '15px', background: NAVY, color: '#fff',
              fontFamily: FONT, fontSize: '15px', fontWeight: 700, border: 'none',
              borderRadius: '10px', cursor: 'pointer', transition: 'background 0.2s, transform 0.15s',
              boxShadow: `0 6px 20px rgba(0,46,83,0.3)`,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#001a33'; e.currentTarget.style.transform = 'translateY(-2px)' }}
            onMouseLeave={e => { e.currentTarget.style.background = NAVY; e.currentTarget.style.transform = 'translateY(0)' }}
          >
            {m.checkBtn}
          </button>
        </div>

        {/* RIGHT — Results */}
        <div>
          {!result && (
            <div style={{
              background: '#fff', borderRadius: '16px', padding: '48px 32px', textAlign: 'center',
              boxShadow: '0 2px 18px rgba(0,46,83,0.07)',
            }}>
              <div style={{ fontSize: '52px', marginBottom: '16px' }}>🏡</div>
              <div style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 600, color: NAVY, marginBottom: '10px' }}>{m.fillDetails}</div>
              <div style={{ fontFamily: FONT, fontSize: '14px', color: '#8a9ab0', lineHeight: '1.7' }}>
                {m.fillDesc}
              </div>
            </div>
          )}

          {result?.error && (
            <div style={{
              background: '#fff3f3', border: '1px solid #fca5a5', borderRadius: '16px',
              padding: '32px', textAlign: 'center',
            }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>⚠️</div>
              <div style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 600, color: '#991b1b' }}>
                {m.errTitle}
              </div>
              <p style={{ fontFamily: FONT, fontSize: '13px', color: '#7f1d1d', marginTop: '8px' }}>
                {m.errDesc}
              </p>
            </div>
          )}

          {result && !result.error && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px', marginBottom: '14px' }}>
                <ResultBox label={m.maxLoan} value={`AED ${fmt(result.maxLoan)}`} highlight />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <ResultBox label={m.maxProperty} value={`AED ${fmt(result.maxPropertyValue)}`} />
                  <ResultBox label={m.minDownPay} value={`AED ${fmt(result.minDownPayment)}`} />
                  <ResultBox label={m.maxTenure} value={`${result.tenorYears} ${m.ageUnit}`} />
                  <ResultBox label={m.maxEMI} value={`AED ${fmt(result.maxEMI)}/mo`} />
                </div>
              </div>

              {/* DBR breakdown */}
              <div style={{ background: '#fff', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,46,83,0.07)' }}>
                <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, marginBottom: '12px' }}>
                  {m.dbrTitle}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { label: m.grossIncome, val: `AED ${fmt(result.grossIncome)}` },
                    { label: m.existingObligs, val: `AED ${fmt(result.totalObligations)}` },
                    { label: m.dbrHeadroom, val: `AED ${fmt(result.maxEMI)}` },
                    { label: m.ltvApplied, val: `${(result.ltvPct * 100).toFixed(0)}%` },
                  ].map(({ label, val }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FONT, fontSize: '13px', padding: '6px 0', borderBottom: '1px solid #f0f3f7' }}>
                      <span style={{ color: '#5a6577' }}>{label}</span>
                      <span style={{ fontWeight: 600, color: NAVY }}>{val}</span>
                    </div>
                  ))}
                </div>
                <p style={{ fontFamily: FONT, fontSize: '11px', color: '#8a9ab0', marginTop: '12px', lineHeight: '1.5' }}>
                  {m.dbrNote}
                </p>
              </div>

              {/* CTA */}
              <div style={{ background: NAVY, borderRadius: '14px', padding: '24px', marginTop: '14px', textAlign: 'center' }}>
                <div style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>
                  {m.readyApply}
                </div>
                <p style={{ fontFamily: FONT, fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px', lineHeight: '1.6' }}>
                  {m.readyApplyDesc}
                </p>
                <a href="/contact"
                  style={{
                    display: 'inline-block', background: GOLD, color: '#fff',
                    fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                    padding: '12px 30px', borderRadius: '8px', textDecoration: 'none',
                  }}>
                  {m.talkAdvisor}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── Main Page ─────────────────────────────────────────────── */
function MortgagePage() {
  const { t } = useLanguage()
  const m = t('mort')
  const SERVICES = m.services
  const [tab, setTab] = useState('calculator') // 'calculator' | 'eligibility'

  return (
    <div className="website-wrapper wpresidence_wrapper_for_header_type1 wide" id="all_wrapper">
      <div className="container-fluid px-0 wpresidence_main_wrapper_class wide has_header_type1">
        <Navbar />
        <main style={{ background: '#f4f7fb', minHeight: '80vh' }}>

          {/* ── Hero Banner ── */}
          <div style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 60%, #0a2d4a 100%)`,
            padding: '150px 20px 80px', textAlign: 'center', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '420px', height: '420px', borderRadius: '50%', background: `${GOLD}12`, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-80px', left: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: `${GOLD}08`, pointerEvents: 'none' }} />

            <span style={{
              display: 'inline-block', background: `${GOLD}20`, color: GOLD,
              fontFamily: FONT, fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px',
              padding: '6px 18px', borderRadius: '50px', marginBottom: '18px',
              textTransform: 'uppercase', border: `1px solid ${GOLD}40`,
            }}>{m.heroBadge}</span>

            <h1 style={{
              fontFamily: FONT, fontSize: 'clamp(30px, 4vw, 52px)', fontWeight: 700,
              color: '#fff', margin: '0 0 16px', letterSpacing: '-0.5px', lineHeight: '1.2',
            }}>
              {m.heroLine1}<br />
              <span style={{ color: GOLD }}>{m.heroAccent}</span>
            </h1>
            <p style={{
              fontFamily: FONT, fontSize: '16px', color: 'rgba(255,255,255,0.72)',
              maxWidth: '580px', margin: '0 auto 36px', lineHeight: '1.7',
            }}>
              {m.heroSub}
            </p>

            {/* EIBOR ticker */}
            <div style={{
              display: 'inline-flex', gap: '24px', background: 'rgba(255,255,255,0.07)',
              border: `1px solid ${GOLD}30`, borderRadius: '50px', padding: '10px 24px',
              flexWrap: 'wrap', justifyContent: 'center',
            }}>
              {[['1M EIBOR', '4.26%'], ['3M EIBOR', '4.24%'], ['6M EIBOR', '3.99%'], ['1Y EIBOR', '4.17%']].map(([label, val]) => (
                <div key={label} style={{ fontFamily: FONT, fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                  <span style={{ color: GOLD, fontWeight: 600 }}>{val}</span> {label}
                </div>
              ))}
            </div>
          </div>

          {/* ── Tabs ── */}
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '44px 20px 60px' }}>

            {/* Tab switcher */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '32px', background: '#fff', padding: '6px', borderRadius: '14px', boxShadow: '0 2px 14px rgba(0,46,83,0.08)', maxWidth: '520px' }}>
              {[
                { key: 'calculator', label: m.tabCalc },
                { key: 'eligibility', label: m.tabElig },
              ].map(t => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)}
                  style={{
                    flex: 1, padding: '13px 12px', border: 'none', borderRadius: '10px', cursor: 'pointer',
                    fontFamily: FONT, fontSize: '14px', fontWeight: 600, transition: 'all 0.2s',
                    background: tab === t.key ? NAVY : 'transparent',
                    color: tab === t.key ? '#fff' : '#8a9ab0',
                  }}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'calculator' && <MortgageCalculator m={m} />}
            {tab === 'eligibility' && <EligibilityCalculator m={m} />}

            {/* ── Services Grid ── */}
            <div style={{ marginTop: '60px' }}>
              <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                <span style={{
                  display: 'inline-block', background: `${GOLD}15`, color: GOLD,
                  fontFamily: FONT, fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px',
                  padding: '5px 16px', borderRadius: '50px', marginBottom: '12px', textTransform: 'uppercase',
                }}>{m.servicesBadge}</span>
                <h2 style={{ fontFamily: FONT, fontSize: '32px', fontWeight: 700, color: NAVY, margin: '0 0 10px', lineHeight: '1.25' }}>
                  {m.servicesHeading}
                </h2>
                <p style={{ fontFamily: FONT, fontSize: '15px', color: '#5a6577', maxWidth: '560px', margin: '0 auto', lineHeight: '1.7' }}>
                  {m.servicesSub}
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                {SERVICES.map((s, i) => (
                  <div key={i}
                    style={{
                      background: '#fff', borderRadius: '14px', padding: '28px 28px',
                      boxShadow: '0 2px 14px rgba(0,46,83,0.06)',
                      borderBottom: `3px solid transparent`,
                      transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                      cursor: 'default',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,46,83,0.12)'; e.currentTarget.style.borderColor = GOLD }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 14px rgba(0,46,83,0.06)'; e.currentTarget.style.borderColor = 'transparent' }}
                  >
                    <div style={{ fontSize: '32px', marginBottom: '14px' }}>{s.icon}</div>
                    <h4 style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>{s.title}</h4>
                    <p style={{ fontFamily: FONT, fontSize: '14px', color: '#5a6577', margin: 0, lineHeight: '1.65' }}>{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── CTA Banner ── */}
            <div style={{
              marginTop: '50px', background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`,
              borderRadius: '20px', padding: '48px 40px', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '24px',
            }}>
              <div>
                <div style={{ fontFamily: FONT, fontSize: '24px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>
                  {m.ctaHeading}
                </div>
                <p style={{ fontFamily: FONT, fontSize: '15px', color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: '1.6', maxWidth: '480px' }}>
                  {m.ctaSub}
                </p>
              </div>
              <a href="/contact"
                style={{
                  display: 'inline-block', background: GOLD, color: '#fff',
                  fontFamily: FONT, fontSize: '15px', fontWeight: 700,
                  padding: '15px 36px', borderRadius: '10px', textDecoration: 'none',
                  whiteSpace: 'nowrap', transition: 'background 0.2s, transform 0.15s',
                  boxShadow: `0 6px 20px rgba(197,160,89,0.4)`,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#d4af6a'; e.currentTarget.style.transform = 'translateY(-2px)' }}
                onMouseLeave={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.transform = 'translateY(0)' }}
              >
                {m.ctaBtn}
              </a>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  )
}

export default MortgagePage
